// Fiche d'une autorisation d'occupation du domaine public : dossier d'origine, titulaire,
// catégorie, surface, période, créance (émise par le module des recettes ou en attente), alertes,
// emprise et position sur la carte ; saisie de l'emprise (polygone) ou de la position par le
// responsable (droit modifier), retrait motivé par l'autorité (droit décider).
import { schemaModificationEmprise, type PolygoneGeoJson } from '@cc/contrats';
import { Bandeau, Bouton, CarteTerritoire, Champ, DialogueConfirmation, Lien, LienBouton, Panneau, PuceEtat, Squelette, ZoneTexte } from '@cc/ui';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date, dateHeure } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_DOMAINE_PUBLIC, modifierEmprise, retirerAutorisation, useAutorisation } from './api';
import { useEchellesTerritoire } from './carte';
import { LIBELLES_ETAT_AUTORISATION, montant } from './logique';
import type { Autorisation } from './types';
import styles from './marches.module.css';

const TON_ETAT: Record<string, 'ok' | 'attente' | 'retard' | 'neutre'> = { active: 'ok', expiree: 'neutre', retiree: 'retard', annulee: 'retard' };

/** Texte d'une emprise : une position « longitude latitude » par ligne ; l'anneau est fermé automatiquement. */
export function empriseVersTexte(e: PolygoneGeoJson | null): string {
  if (!e) return '';
  const anneau = e.coordinates[0] ?? [];
  return anneau.slice(0, -1).map(([lon, lat]) => `${lon} ${lat}`).join('\n');
}

export function texteVersEmprise(texte: string): PolygoneGeoJson | null {
  const points = texte
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.split(/[\s,;]+/).map(Number));
  if (points.length === 0) return null;
  const anneau = points.map(([lon, lat]) => [lon ?? NaN, lat ?? NaN] as [number, number]);
  const premier = anneau[0] as [number, number];
  const dernier = anneau[anneau.length - 1] as [number, number];
  if (premier[0] !== dernier[0] || premier[1] !== dernier[1]) anneau.push([premier[0], premier[1]]);
  return { type: 'Polygon', coordinates: [anneau] };
}

function Emprise({ a }: { a: Autorisation }) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState(() => empriseVersTexte(a.emprise));
  const [position, setPosition] = useState({ longitude: a.position ? String(a.position.longitude) : '', latitude: a.position ? String(a.position.latitude) : '' });
  const [probleme, setProbleme] = useState<string | null>(null);
  const action = useAction({
    executer: () => {
      const emprise = texteVersEmprise(texte);
      const pos = position.longitude && position.latitude ? { longitude: Number(position.longitude), latitude: Number(position.latitude) } : null;
      const v = schemaModificationEmprise.safeParse({ emprise, position: pos });
      if (!v.success) {
        setProbleme(v.error.issues.map((i) => `${i.path.join('.') || 'emprise'} : ${i.message}`).join(' ; '));
        return Promise.reject(new Error('Saisie incomplète'));
      }
      setProbleme(null);
      return modifierEmprise(a.id, v.data);
    },
    succes: 'Emprise enregistrée.',
    invalider: INVALIDER_DOMAINE_PUBLIC,
    apres: () => setOuvert(false),
  });
  return (
    <>
      <Bouton variante="secondaire" onClick={() => setOuvert(true)}>
        Saisir l&apos;emprise
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`Emprise de l'autorisation ${a.numero}`}
        message={<p>Polygone en WGS84 : une position « longitude latitude » par ligne, quatre sommets au moins, sans répéter le premier ; le serveur vérifie la validité géométrique. La position seule suffit pour un point.</p>}
        libelleConfirmer="Enregistrer"
        enCours={action.enCours}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        <ZoneTexte libelle="Sommets de l'emprise" value={texte} onChange={(e) => setTexte(e.target.value)} rows={6} facultatif aide="Exemple : 9.4536 0.3925" />
        <Champ libelle="Longitude de la position" value={position.longitude} onChange={(e) => setPosition((p) => ({ ...p, longitude: e.target.value }))} facultatif inputMode="decimal" />
        <Champ libelle="Latitude de la position" value={position.latitude} onChange={(e) => setPosition((p) => ({ ...p, latitude: e.target.value }))} facultatif inputMode="decimal" />
        {probleme ? <Bandeau type="erreur">{probleme}</Bandeau> : null}
        {action.messageErreur && action.messageErreur !== 'Saisie incomplète' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

function Retrait({ a }: { a: Autorisation }) {
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () => retirerAutorisation(a.id, motif.trim()),
    succes: 'Autorisation retirée ; les créances non payées sont annulées.',
    invalider: INVALIDER_DOMAINE_PUBLIC,
    apres: () => setOuvert(false),
  });
  return (
    <>
      <Bouton variante="danger" onClick={() => setOuvert(true)}>
        Retirer
      </Bouton>
      <DialogueConfirmation
        ouvert={ouvert}
        titre={`Retirer l'autorisation ${a.numero}`}
        message={<p>Le retrait est l&apos;acte de l&apos;autorité qui a délivré l&apos;autorisation ; le motif est conservé et les créances non payées sont annulées par le module des recettes.</p>}
        libelleConfirmer="Retirer"
        variante="danger"
        enCours={action.enCours}
        confirmationDesactivee={motif.trim().length < 5}
        onConfirmer={() => action.lancer(undefined)}
        onAnnuler={() => {
          setOuvert(false);
          action.reinitialiser();
        }}
      >
        <ZoneTexte libelle="Motif (cinq caractères au moins)" value={motif} onChange={(e) => setMotif(e.target.value)} required erreur={action.erreursChamps.find((c) => c.champ === 'motif')?.message} />
        {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}

function CarteAutorisation({ a }: { a: Autorisation }) {
  const t = useEchellesTerritoire();
  const point = a.position ?? (a.emprise ? { longitude: (a.emprise.coordinates[0] ?? []).reduce((s, p) => s + (p[0] ?? 0), 0) / Math.max(1, (a.emprise.coordinates[0] ?? []).length), latitude: (a.emprise.coordinates[0] ?? []).reduce((s, p) => s + (p[1] ?? 0), 0) / Math.max(1, (a.emprise.coordinates[0] ?? []).length) } : null);
  if (!point) return <p className={styles.aide}>Aucune emprise ni position : l&apos;autorisation n&apos;apparaît pas sur la carte.</p>;
  if (!t.pret || !t.echelles || !t.echelle) return <Squelette hauteurBloc={300} libelle="Chargement de la carte" />;
  return (
    <CarteTerritoire
      titre={`Emprise de l'autorisation ${a.numero}`}
      echelles={t.echelles.echelles}
      echelle={t.echelle}
      onEchelle={t.setEchelle}
      marqueurs={[{ id: a.id, numero: 1, longitude: point.longitude, latitude: point.latitude, libelle: `${a.numero} · ${a.categorie_libelle}`, ton: 'autre' }]}
      surfaces={a.emprise ? { type: 'FeatureCollection', features: [{ type: 'Feature', id: a.id, geometry: a.emprise, properties: { numero: a.numero } }] } : null}
      libellesTon={{ autre: 'Autorisation' }}
      libelleObjets={{ singulier: 'autorisation', pluriel: 'autorisations' }}
      hauteur={320}
      compact
    />
  );
}

export function PageAutorisation() {
  const { id = '' } = useParams();
  const { contexte } = useSession();
  const autorisation = useAutorisation(id);
  const a = autorisation.data;
  return (
    <>
      <EnTetePage
        etiquette={a ? `${a.categorie_libelle} · ${a.surface_m2.toLocaleString('fr-FR')} m²` : 'Autorisation'}
        titre={a ? `Autorisation ${a.numero}` : 'Autorisation'}
        description={a ? <span>{a.titulaire.nom}{a.enseigne ? ` (${a.enseigne})` : ''}{a.adresse ? `, ${a.adresse}` : ''}. Du {date(a.periode.debut)} au {date(a.periode.fin)}.</span> : null}
        actions={
          <>
            <LienBouton href="/marches/domaine-public" icone={ArrowLeft}>
              Domaine public
            </LienBouton>
            {a && a.etat === 'active' && peut(contexte, 'domaine_public', 'modifier') ? <Emprise a={a} /> : null}
            {a && a.etat === 'active' && peut(contexte, 'domaine_public', 'decider') ? <Retrait a={a} /> : null}
          </>
        }
      />
      <EtatRequete chargement={autorisation.isPending} erreur={autorisation.error} onReessayer={() => void autorisation.refetch()} lot="L2-B" fonction="La fiche de l'autorisation">
        {a ? (
          <div className={styles.disposition}>
            <div className={styles.pile}>
              <Panneau titre="Emprise" niveau={3}>
                <CarteAutorisation a={a} />
                <p className={styles.aide}>
                  {a.emprise ? `Emprise saisie : ${Math.max(0, (a.emprise.coordinates[0]?.length ?? 1) - 1)} sommets.` : 'Aucune emprise saisie.'}{' '}
                  {a.position ? `Position : ${a.position.longitude.toFixed(5)}, ${a.position.latitude.toFixed(5)}.` : 'Aucune position.'}
                </p>
              </Panneau>
              {a.fin_anticipee ? (
                <Bandeau type="attention" titre={`Autorisation ${LIBELLES_ETAT_AUTORISATION[a.etat].toLowerCase()}`}>
                  Motif : {a.fin_anticipee.motif}
                  {a.fin_anticipee.le ? `, le ${dateHeure(a.fin_anticipee.le)}` : ''}
                  {a.fin_anticipee.creances_annulees ? `, ${a.fin_anticipee.creances_annulees} créance${a.fin_anticipee.creances_annulees > 1 ? 's' : ''} annulée${a.fin_anticipee.creances_annulees > 1 ? 's' : ''}` : ''}.
                </Bandeau>
              ) : null}
            </div>
            <aside className={styles.cote} aria-label="Situation de l'autorisation">
              <Panneau titre="Situation" niveau={3}>
                <dl className={styles.definitions}>
                  <dt>État</dt>
                  <dd>
                    <PuceEtat ton={TON_ETAT[a.etat] ?? 'neutre'} libelle={LIBELLES_ETAT_AUTORISATION[a.etat]} />
                  </dd>
                  <dt>Dossier</dt>
                  <dd>
                    <Lien href={`/dossiers/${a.dossier.id}`} className="cc-mono">
                      {a.dossier.numero ?? 'dossier'}
                    </Lien>
                    , décidé le {dateHeure(a.decide_le)}
                  </dd>
                  <dt>Titulaire</dt>
                  <dd>
                    {a.titulaire.nom}
                    {a.titulaire.usager_compte_id ? ', compte portail' : ''}
                  </dd>
                  <dt>Créance</dt>
                  <dd>
                    {a.creance.statut === 'emise' ? (
                      <>
                        <span className="cc-mono">{a.creance.numero}</span> · <span className={styles.montant}>{montant(a.creance.montant_du)}</span>
                        {a.creance.echeance ? `, échéance ${date(a.creance.echeance)}` : ''}
                        {a.creance.regle ? <span className={styles.regle}>{a.creance.regle}</span> : null}
                      </>
                    ) : (
                      <span className={styles.attention}>en attente du module des recettes (redemandée chaque jour)</span>
                    )}
                  </dd>
                  <dt>Rubrique</dt>
                  <dd className="cc-mono">{a.rubrique_code}</dd>
                  <dt>Alertes</dt>
                  <dd>
                    {a.alertes.j30 ? `30 jours : ${dateHeure(a.alertes.j30)}` : '30 jours : non envoyée'} ; {a.alertes.j7 ? `7 jours : ${dateHeure(a.alertes.j7)}` : '7 jours : non envoyée'}
                  </dd>
                </dl>
              </Panneau>
            </aside>
          </div>
        ) : null}
      </EtatRequete>
    </>
  );
}
